/**
 * Regio-indeling van Zuid-Holland voor de interne structuur van de site.
 *
 * Doel is niet extra pagina's maken, maar de bestaande plaats- en
 * gemeentepagina's begrijpelijk groeperen: Zuid-Holland -> regio -> gemeente ->
 * plaats. De indeling volgt de gangbare geografische regio's; iedere gemeente
 * komt exact één keer voor. Gemeenten die hier (nog) niet in staan vallen onder
 * "Overig Zuid-Holland".
 */

import { isGemeenteIndexable } from "@/lib/gemeente-canonical";
import { GEMEENTEN } from "./local-seo-gemeenten";
import type { LocalPlace } from "./local-seo";

export type ZhRegion = {
  slug: string;
  name: string;
  /** Engelse benaming voor de /en-pagina's. */
  nameEn: string;
  /** Korte, feitelijke omschrijving van de regio. */
  blurb: string;
  /** Slugs van gemeenten binnen deze regio. */
  gemeenten: string[];
};

export const ZH_REGIONS: ZhRegion[] = [
  {
    slug: "rotterdam-rijnmond",
    name: "Rotterdam en Rijnmond",
    nameEn: "Rotterdam and the Rijnmond area",
    blurb:
      "Rotterdam en de omliggende gemeenten langs de Nieuwe Maas en de Hollandse IJssel.",
    gemeenten: [
      "rotterdam",
      "schiedam",
      "vlaardingen",
      "maassluis",
      "capelle-aan-den-ijssel",
      "krimpen-aan-den-ijssel",
      "barendrecht",
      "albrandswaard",
      "ridderkerk",
      "lansingerland",
    ],
  },
  {
    slug: "haaglanden",
    name: "Haaglanden en Delft",
    nameEn: "The Hague, Delft and Haaglanden",
    blurb: "Den Haag, Delft, Zoetermeer, het Westland en de gemeenten daartussen.",
    gemeenten: [
      "den-haag",
      "rijswijk",
      "leidschendam-voorburg",
      "wassenaar",
      "delft",
      "midden-delfland",
      "pijnacker-nootdorp",
      "westland",
      "zoetermeer",
    ],
  },
  {
    slug: "leiden-bollenstreek",
    name: "Leiden, Bollenstreek en Rijnstreek",
    nameEn: "Leiden, the Bulb Region and the Rhine area",
    blurb: "Leiden, de kustplaatsen, de Bollenstreek en het gebied langs de Oude Rijn.",
    gemeenten: [
      "leiden",
      "leiderdorp",
      "oegstgeest",
      "voorschoten",
      "zoeterwoude",
      "katwijk",
      "noordwijk",
      "lisse",
      "hillegom",
      "teylingen",
      "kaag-en-braassem",
      "alphen-aan-den-rijn",
      "nieuwkoop",
    ],
  },
  {
    slug: "drechtsteden-alblasserwaard",
    name: "Drechtsteden en Alblasserwaard",
    nameEn: "Dordrecht, the Drechtsteden and Alblasserwaard",
    blurb: "Dordrecht en de gemeenten langs de Beneden-Merwede en de Noord.",
    gemeenten: [
      "dordrecht",
      "zwijndrecht",
      "papendrecht",
      "sliedrecht",
      "hendrik-ido-ambacht",
      "alblasserdam",
      "molenlanden",
      "hardinxveld-giessendam",
      "gorinchem",
    ],
  },
  {
    slug: "midden-holland",
    name: "Midden-Holland",
    nameEn: "Midden-Holland (Gouda area)",
    blurb: "Gouda en de omliggende gemeenten in het veenweidegebied.",
    gemeenten: [
      "gouda",
      "waddinxveen",
      "zuidplas",
      "bodegraven-reeuwijk",
      "krimpenerwaard",
    ],
  },
  {
    slug: "zuid-hollandse-eilanden",
    name: "Zuid-Hollandse eilanden",
    nameEn: "the South Holland islands",
    blurb: "Voorne-Putten, de Hoeksche Waard en Goeree-Overflakkee.",
    gemeenten: ["voorne-aan-zee", "nissewaard", "hoeksche-waard", "goeree-overflakkee"],
  },
];

const REGION_BY_GEMEENTE = new Map<string, ZhRegion>();
for (const region of ZH_REGIONS) {
  for (const slug of region.gemeenten) REGION_BY_GEMEENTE.set(slug, region);
}

/** Regio van een gemeente, of null als de gemeente niet is ingedeeld. */
export function regionForGemeente(gemeenteSlug: string | undefined): ZhRegion | null {
  if (!gemeenteSlug) return null;
  return REGION_BY_GEMEENTE.get(gemeenteSlug) ?? null;
}

type PlaceRef = Pick<LocalPlace, "slug" | "province" | "gemeente" | "gemeenteSlug">;

function slugifyGemeente(name: string): string {
  return name
    .replace(/^Gemeente\s+/i, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/['’.]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Gemeenteslug van een plaats. Niet elke plaats heeft het veld gemeenteSlug
 * gevuld; dan zoeken wij de gemeente via haar woonplaatsenlijst of via de naam.
 */
export function gemeenteSlugForPlace(place: PlaceRef): string | null {
  if (place.gemeenteSlug) return place.gemeenteSlug;
  const byPlaces = GEMEENTEN.find((g) => g.places.includes(place.slug));
  if (byPlaces) return byPlaces.slug;
  const bySlug = slugifyGemeente(place.gemeente);
  return bySlug || null;
}

/** Gemeentepagina van een plaats, of null als die pagina niet bestaat. */
export function gemeentePageForPlace(place: PlaceRef) {
  const slug = gemeenteSlugForPlace(place);
  // Gemeenten met dezelfde naam als een stadspagina hebben geen eigen pagina meer.
  if (!isGemeenteIndexable(slug)) return null;
  return (slug && GEMEENTEN.find((g) => g.slug === slug)) || null;
}

/** Regio van een plaats (alleen Zuid-Holland), of null. */
export function regionForPlace(place: PlaceRef): ZhRegion | null {
  if (place.province !== "Zuid-Holland") return null;
  return regionForGemeente(gemeenteSlugForPlace(place) ?? undefined);
}

/** Gemeenten van een regio die daadwerkelijk een pagina hebben. */
export function gemeentenInRegion(region: ZhRegion) {
  return GEMEENTEN.filter((g) => region.gemeenten.includes(g.slug)).sort((a, b) =>
    a.name.localeCompare(b.name, "nl"),
  );
}

/** Zuid-Hollandse gemeenten met pagina die in geen regio zijn ingedeeld. */
export function ungroupedGemeenten() {
  return GEMEENTEN.filter((g) => !REGION_BY_GEMEENTE.has(g.slug)).sort((a, b) =>
    a.name.localeCompare(b.name, "nl"),
  );
}
